import { CellValueChangedEvent } from 'ag-grid-community';
import { useAtomValue, useStore } from 'jotai';
import { useCallback, useRef } from 'react';
import { ICompanyFinancialsDataModel } from '../../../../../data-models/company-financials.data-model';
import { RendererType } from '../../../../../data-models/field-definition';
import { IField } from '../../../../../data-models/field2.data-model';
import { isInvalidDate } from '../../../../../util/date-utilts';
import { isInvalidNumber } from '../../../../KPI/components/KPIRequests/KPIRequestResponse/KPIAgGrid';
import { allCompanyFinancialsState } from '../../../state/CompanyFinancialsState';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';
import {
  cellRow,
  FinancialsGridRowData,
  getFinancialAt,
  resolveCellKey,
} from '../components/FinancialsGrid2Defs';
import { selectedFinancialsCurrencyForIdAtom } from './FinancialsCurrencyState';
import { ICustomKPIActions, useBulkCustomKPIActions } from './useCustomKPIActions';
import { toPayloadValue } from './useFinancialsColumnDefs';

export interface IFinancialsCellEdit {
  companyId: number;
  row: Pick<FinancialsGridRowData, 'kpiId' | 'period' | 'section'>;
  kpi: IField<unknown> | undefined;
  isoDate: string;
  newValue: unknown;
  existing: ICompanyFinancialsDataModel | undefined;
  selectedCurrency: string;
}

// the BE's isMonetaryFormMeta rule: only these KPIs are FX-converted, yet any currency sent is stored verbatim
function isMonetaryKpi(kpi: IField<unknown> | undefined) {
  const formMeta = kpi?.formMeta;
  return formMeta?.renderer?.type === RendererType.currency || formMeta?.formatter?.id === 'currency';
}

/** One edited cell → the create, update or delete it needs; null when there is nothing to write. */
export function buildCellSaveActions({
  companyId,
  row,
  kpi,
  isoDate,
  newValue,
  existing,
  selectedCurrency,
}: IFinancialsCellEdit): ICustomKPIActions | null {
  const none: ICustomKPIActions = { companyId, create: [], update: [], delete: [] };
  if (newValue == null || String(newValue).trim() === '') {
    return existing ? { ...none, delete: [[companyId, existing.id]] } : null;
  }
  const value = toPayloadValue(newValue);
  // no currencyCode on update: the BE keeps the currency the item was reported in
  if (existing) {
    return { ...none, update: [[companyId, existing.id, { date: isoDate, section: row.section, value }]] };
  }
  const { kpiId, period, section } = row;
  const item = { companyId, kpiId, date: isoDate, period, section, value };
  // a new monetary item without a currencyCode is stored as USD by the BE, whatever the company reports in
  const created = isMonetaryKpi(kpi) ? { ...item, currencyCode: selectedCurrency } : item;
  return { ...none, create: [[companyId, created]] };
}

/** A cell across renders: the row it belongs to and the date its column names. */
function cellSaveId(row: Pick<FinancialsGridRowData, 'kpiId' | 'period' | 'section'>, isoDate: string) {
  return `${row.kpiId}|${row.period}|${row.section}|${isoDate}`;
}

/** The cell's row as the financials state holds it, which is the only place its stored id can be read. */
function storedFinancialAt(
  financials: ICompanyFinancialsDataModel[],
  row: Pick<FinancialsGridRowData, 'kpiId' | 'period' | 'section'>,
  isoDate: string
) {
  return financials.find(
    (item) =>
      item.kpiId === row.kpiId &&
      item.period === row.period &&
      item.section === row.section &&
      item.date === isoDate
  );
}

/** Saves each single-cell edit of the Company Profile › Financials grid through the optimistic bulk writer. */
export function useFinancialsGrid2Save(
  financialsByKey: Record<string, unknown>,
  kpisById: Map<number, IField<unknown>>
) {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const selectedCurrency = useAtomValue(selectedFinancialsCurrencyForIdAtom(companyId));
  const bulkUpdate = useBulkCustomKPIActions();
  const store = useStore();
  // Writes to one cell run in order, because until a write comes back the row it is making carries the
  // optimistic writer's client-side id: acting on that id would 404, and two writes sent at once would land
  // in whichever order the network chose.
  const pendingRef = useRef(new Map<string, Promise<unknown>>());

  return useCallback(
    (event: CellValueChangedEvent<FinancialsGridRowData>) => {
      const field = event.colDef.field;
      if (!field) {
        return;
      }
      // the date editor can hand over an invalid Date; that must not clear the stored value
      if (isInvalidDate(event.newValue) || isInvalidNumber(event.newValue)) {
        event.node.setData({ ...event.data, [field]: event.oldValue });
        return;
      }
      // a pinned fiscal-year cell saves a yearly item at its year end, whatever period its row shows
      const key = resolveCellKey(field, event.data);
      if (!key) {
        return;
      }
      const row = cellRow(event.data, key);
      const cellId = cellSaveId(row, key.date);
      const pending = pendingRef.current.get(cellId);
      const write = (async () => {
        let existing = getFinancialAt(financialsByKey, row, key.date);
        if (pending) {
          // wait for the write in front, then take the row it left behind: the grid is still showing the
          // optimistic stand-in, and a clear has nothing to delete until the create it follows has landed
          await pending;
          existing = storedFinancialAt(await store.get(allCompanyFinancialsState(companyId)), row, key.date);
        }
        const actions = buildCellSaveActions({
          companyId,
          row,
          kpi: kpisById.get(event.data.kpiId),
          isoDate: key.date,
          newValue: event.newValue,
          existing,
          selectedCurrency,
        });
        if (actions) {
          await bulkUpdate(actions);
        }
      })();
      // the queue chains on the handled promise, so one failed write does not reject the ones behind it
      const settled = write.catch((err: unknown) => console.error(err));
      pendingRef.current.set(cellId, settled);
      void settled.finally(() => {
        if (pendingRef.current.get(cellId) === settled) {
          pendingRef.current.delete(cellId);
        }
      });
    },
    [bulkUpdate, companyId, financialsByKey, kpisById, selectedCurrency, store]
  );
}
